import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { ROUTE_OWNER_DASHBOARD } from '../lib/roleContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Calendar, CreditCard, Check, X, Clock, AlertTriangle, 
  MapPin, Users, Home, ArrowRight, RefreshCw, Loader2, 
  Euro, AlertCircle, XCircle, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import {
  displayText,
  formatDateTime,
  formatDurationLabel,
  formatEstimatedTotal,
  formatInquiryDate,
  getInquiryRecordId,
  listingLocation,
  listingTitle,
  shortId,
  viewingStateLabel,
} from '../lib/bookingInquiryDisplay';
import {
  buildListingDetailUrlFromInquiry,
  inquiryCardDomId,
  parseFocusInquiryIdFromSearchParams,
} from '../lib/bookingInquiryContext';
import { translateTemplate } from '../lib/publicListingTruth';
import {
  scrollToInquiryCard,
  shouldRestoreBookingsFocus,
} from '../lib/bookingInquiryFocus';
import GuestViewingSlotSelector from '../components/bookings/GuestViewingSlotSelector';
import {
  getMyBookingsOwnerCtaLayout,
  isOwnerBookingsContext,
  MY_BOOKINGS_OWNER_BANNER_TEST_ID,
} from '../lib/myBookingsOwnerContext';
import { arePaymentsEnabled, PAYMENTS_UNAVAILABLE_TEST_ID } from '../lib/paymentsAvailability';
import { bookingDepositStatusAPI, isPaymentConfirmed } from '../lib/bookingDepositStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const paymentsEnabled = arePaymentsEnabled();

export default function MyBookingsPage() {
  const { user, isAuthenticated, sessionRevision } = useAuth();
  const { isGuestContext, canAccessOwner } = useRoleContext();
  const ownerBookingsContext = isOwnerBookingsContext({ canAccessOwner, isGuestContext });
  const ownerCtaLayout = getMyBookingsOwnerCtaLayout({ canAccessOwner, isGuestContext });
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(null);
  const [refundPreview, setRefundPreview] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [editForm, setEditForm] = useState({ check_in_date: '', check_out_date: '', guests: 1, message: '', special_requests: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const focusInquiryId = parseFocusInquiryIdFromSearchParams(searchParams);
  const didRestoreScrollRef = useRef(false);

  // Check for payment success/cancel from URL params (only when payments enabled)
  useEffect(() => {
    if (!paymentsEnabled) {
      return;
    }
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error(t('booking.toast.payment_cancelled', 'Payment was cancelled'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    setInquiries([]);
    setLoading(true);
    didRestoreScrollRef.current = false;
    loadInquiries();
  }, [isAuthenticated, user?.id, sessionRevision]);

  useEffect(() => {
    if (loading || didRestoreScrollRef.current || !focusInquiryId) {
      return;
    }
    if (!shouldRestoreBookingsFocus(focusInquiryId, inquiries)) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (scrollToInquiryCard(focusInquiryId)) {
        didRestoreScrollRef.current = true;
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [loading, focusInquiryId, inquiries]);

  const loadInquiries = async () => {
    try {
      const res = await api.get('/bookings/my-inquiries');
      setInquiries(res.data.inquiries || []);
    } catch (error) {
      console.error('Failed to load inquiries:', error);
      toast.error(t('booking.toast.load_failed', 'Failed to load your bookings'));
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await bookingDepositStatusAPI.get(sessionId);
      if (isPaymentConfirmed(res.data)) {
        toast.success(t('booking.toast.payment_success', 'Payment successful! Your booking is confirmed.'));
        loadInquiries();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error(
        error.response?.data?.detail
        || t('booking.toast.payment_status_failed', 'Could not verify your payment. Please refresh the page or contact support.'),
      );
    }
  };

  const handlePayDeposit = async (inquiry) => {
    if (!paymentsEnabled) {
      toast.info(t('payments.booking_deposit_unavailable', 'Deposit payments are not available yet. Please contact support.'));
      return;
    }
    if (!isAuthenticated) {
      toast.error(t('booking.toast.sign_in_for_deposit', 'Please sign in to pay deposit'));
      navigate('/login');
      return;
    }

    setProcessingPayment(inquiry.id);
    
    try {
      const res = await api.post('/payments/booking-deposit', {
        inquiry_id: inquiry.id,
        origin_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(t('booking.toast.payment_session_failed', 'Failed to create payment session'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || t('booking.toast.payment_process_failed', 'Failed to process payment'));
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleWithdrawInquiry = async (inquiryId) => {
    if (!confirm(t('booking.withdraw_confirm', 'Withdraw this inquiry? The owner will no longer see it as an active request.'))) return;

    try {
      await api.delete(`/bookings/inquiry/${inquiryId}`);
      toast.success(t('booking.inquiry_withdrawn', 'Inquiry withdrawn'));
      loadInquiries();
    } catch (error) {
      console.error('Withdraw error:', error);
      toast.error(error.response?.data?.detail || t('booking.toast.withdraw_failed', 'Failed to withdraw inquiry'));
    }
  };

  const openEditInquiry = (inquiry) => {
    setEditingInquiry(inquiry);
    setEditForm({
      check_in_date: (inquiry.check_in_date || '').slice(0, 10),
      check_out_date: (inquiry.check_out_date || '').slice(0, 10),
      guests: inquiry.guests || 1,
      message: inquiry.message || '',
      special_requests: inquiry.special_requests || '',
    });
  };

  const handleSaveInquiryEdit = async () => {
    if (!editingInquiry) return;
    setSavingEdit(true);
    try {
      await api.patch(`/bookings/inquiry/${editingInquiry.id}`, editForm);
      toast.success(t('booking.inquiry_updated', 'Inquiry updated'));
      setEditingInquiry(null);
      loadInquiries();
    } catch (error) {
      console.error('Edit error:', error);
      toast.error(error.response?.data?.detail || t('booking.toast.update_failed', 'Failed to update inquiry'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handlePreviewRefund = async (inquiry) => {
    if (!paymentsEnabled) {
      toast.info(t('payments.refund_unavailable', 'Online refunds are not available yet. Please contact support.'));
      return;
    }
    setShowRefundModal(inquiry);
    setLoadingRefund(true);
    
    try {
      const res = await api.post('/payments/refund-preview', {
        inquiry_id: inquiry.id
      });
      setRefundPreview(res.data);
    } catch (error) {
      console.error('Refund preview error:', error);
      toast.error(error.response?.data?.detail || t('booking.toast.refund_calculate_failed', 'Failed to calculate refund'));
      setShowRefundModal(null);
    } finally {
      setLoadingRefund(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!showRefundModal) return;
    
    setLoadingRefund(true);
    try {
      const res = await api.post('/payments/refund', {
        inquiry_id: showRefundModal.id,
        reason: 'User requested cancellation'
      });
      
      toast.success(
        translateTemplate(
          t('booking.toast.refund_processed', 'Refund processed! €{amount} will be returned.'),
          { amount: res.data.refund_amount.toFixed(2) },
        ),
      );
      setShowRefundModal(null);
      setRefundPreview(null);
      loadInquiries();
    } catch (error) {
      console.error('Refund error:', error);
      toast.error(error.response?.data?.detail || t('booking.toast.refund_process_failed', 'Failed to process refund'));
    } finally {
      setLoadingRefund(false);
    }
  };

  const getRefundTypeLabel = (refundType) => {
    const key = `booking.refund_type.${refundType}`;
    return t(key, refundType);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
        icon: Clock,
        label: t('booking.inquiry_status_pending', 'Awaiting owner response'),
      },
      approved: {
        color: 'bg-green-500/20 text-green-400 border-green-500/30',
        icon: Check,
        label: t('booking.status_approved_pay_deposit', 'Approved - Pay Deposit'),
      },
      declined: {
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        icon: X,
        label: t('booking.inquiry_status_declined', 'Declined'),
      },
      confirmed: {
        color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        icon: Check,
        label: t('booking.inquiry_status_confirmed', 'Confirmed'),
      },
      cancelled: {
        color: 'bg-slate-500/20 text-slate-600 border-slate-500/30',
        icon: XCircle,
        label: t('booking.status_withdrawn', 'Withdrawn'),
      },
      refunded: {
        color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
        icon: RefreshCw,
        label: t('booking.inquiry_status_refunded', 'Refunded'),
      },
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (dateStr) => {
    return (
      formatInquiryDate(dateStr)
      || t('booking.date_unavailable', 'Date unavailable')
    );
  };

  const formatTimestamp = (isoStr) => {
    return (
      formatDateTime(isoStr)
      || t('booking.not_yet_updated', 'Not yet updated')
    );
  };

  const DetailCell = ({ label, value, title }) => (
    <div className="min-w-0">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate" title={title || (typeof value === 'string' ? value : undefined)}>
        {value}
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 max-w-md mx-auto">
          <CardHeader className="text-center">
            <Calendar className="w-12 h-12 text-gold-400 mx-auto mb-4" />
            <CardTitle className="text-slate-900">{t('booking.sign_in_title', 'Sign In Required')}</CardTitle>
            <CardDescription>{t('booking.sign_in_desc', 'Please sign in to view your booking requests')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-gold-600 to-amber-600 hover:from-gold-500 hover:to-amber-500"
            >
              {t('booking.sign_in_action', 'Sign In')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20 pb-8 px-4" data-testid="my-bookings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('booking.page_title', 'My Bookings')}</h1>
          <p className="text-slate-600">{t('booking.page_subtitle', 'Track your booking requests and manage payments')}</p>
        </div>

        {!paymentsEnabled && (
          <div
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid={PAYMENTS_UNAVAILABLE_TEST_ID}
          >
            {t(
              'payments.booking_payments_unavailable',
              'Deposit payments and online refunds are not available yet. Contact support if you need help with a booking.',
            )}
          </div>
        )}

        {ownerCtaLayout.showTopOwnerBanner && (
          <div
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid={MY_BOOKINGS_OWNER_BANNER_TEST_ID}
          >
            <p className="text-sm text-amber-900">
              {t(
                'booking.owner_guest_page_banner',
                'My Bookings shows requests you made as a client. Owner inquiries are managed in Owner Dashboard.',
              )}
            </p>
            <Button
              asChild
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-100 shrink-0"
            >
              <Link to={ROUTE_OWNER_DASHBOARD}>
                {t('booking.owner_dashboard_link', 'Owner Dashboard')}
              </Link>
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: t('booking.stat_total', 'Total'), count: inquiries.length, color: 'text-slate-600' },
            { label: t('analytics.pending', 'Pending'), count: inquiries.filter(i => i.status === 'pending').length, color: 'text-amber-400' },
            { label: t('booking.stat_awaiting_payment', 'Awaiting Payment'), count: inquiries.filter(i => i.status === 'approved' && !i.deposit_paid).length, color: 'text-green-400' },
            { label: t('analytics.confirmed', 'Confirmed'), count: inquiries.filter(i => i.status === 'confirmed').length, color: 'text-emerald-400' }
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.count}</div>
              <div className="text-sm text-slate-800">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gold-400 animate-spin" />
          </div>
        ) : inquiries.length === 0 ? (
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200 text-center py-12">
            <CardContent>
              <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              {ownerBookingsContext ? (
                <>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {t('booking.owner_personal_empty_title', 'No personal booking requests yet')}
                  </h3>
                  <p className="text-slate-600">
                    {t(
                      'booking.owner_personal_empty_desc',
                      'When you request to book a listing as a guest, your requests will appear here.',
                    )}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {t('booking.empty_title', 'No Booking Requests Yet')}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {t('booking.empty_desc', 'Browse listings and request to book your next stay')}
                  </p>
                  <Button
                    onClick={() => navigate('/listings')}
                    className="bg-gradient-to-r from-gold-600 to-amber-600"
                  >
                    {t('favorites.browse_listings', 'Browse Properties')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => {
              const inquiryRecordId = getInquiryRecordId(inquiry);
              const statusConfig = getStatusConfig(inquiry.status);
              const StatusIcon = statusConfig.icon;
              const needsPayment = inquiry.status === 'approved' && !inquiry.deposit_paid;
              const canWithdraw = inquiry.status === 'pending' && !inquiry.responded_at;
              const canEdit = canWithdraw;
              const canRefund = inquiry.status === 'confirmed' && inquiry.deposit_paid && !inquiry.refund_processed;
              
              const title = listingTitle(inquiry, t);
              const location = listingLocation(inquiry, t);
              const inquiryRef = shortId(inquiry.inquiry_ref || inquiryRecordId) || t('booking.date_unavailable', '—');
              const listingRef = shortId(inquiry.listing_ref || inquiry.listing_id) || t('booking.date_unavailable', '—');
              const estimated = formatEstimatedTotal(inquiry);
              const propertyType = displayText(
                inquiry.listing_property_type,
                t('booking.date_unavailable', '—'),
              );

              return (
                <Card
                  key={inquiryRecordId}
                  id={inquiryCardDomId(inquiryRecordId)}
                  data-inquiry-id={inquiryRecordId}
                  tabIndex={-1}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden scroll-mt-24"
                  data-testid={`booking-${inquiryRecordId}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="flex-1 p-6 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                          <div className="min-w-0 flex-1">
                            <Link
                              to={buildListingDetailUrlFromInquiry(inquiry)}
                              className="text-lg font-semibold text-slate-900 hover:text-amber-700 transition-colors block truncate"
                              title={title}
                            >
                              {title}
                            </Link>
                            <div className="flex items-center gap-2 text-slate-600 text-sm mt-1">
                              <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                              <span className="truncate" title={location}>{location}</span>
                            </div>
                            {inquiry.listing_property_type && (
                              <div className="flex items-center gap-2 text-slate-500 text-xs mt-1">
                                <Home className="w-3.5 h-3.5 shrink-0" aria-hidden />
                                <span>{t('booking.property_type', 'Property type')}: {propertyType}</span>
                              </div>
                            )}
                          </div>
                          <Badge className={`${statusConfig.color} border shrink-0 self-start`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-4 pb-4 border-b border-slate-100">
                          <span title={inquiryRecordId}>
                            {t('booking.inquiry_ref', 'Inquiry')}: <span className="font-mono text-slate-700">{inquiryRef}</span>
                          </span>
                          <span title={inquiry.listing_id}>
                            {t('booking.listing_ref', 'Listing')}: <span className="font-mono text-slate-700">{listingRef}</span>
                          </span>
                          <span title={inquiry.created_at}>
                            {t('booking.created_at', 'Created')}: <span className="text-slate-700">{formatTimestamp(inquiry.created_at)}</span>
                          </span>
                          <span title={inquiry.updated_at || inquiry.created_at}>
                            {t('booking.updated_at', 'Updated')}: <span className="text-slate-700">{formatTimestamp(inquiry.updated_at)}</span>
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <DetailCell
                            label={t('booking.check_in', 'Check-in')}
                            value={formatDate(inquiry.check_in_date)}
                            title={inquiry.check_in_date}
                          />
                          <DetailCell
                            label={t('booking.check_out', 'Check-out')}
                            value={formatDate(inquiry.check_out_date)}
                            title={inquiry.check_out_date}
                          />
                          <DetailCell
                            label={t('booking.duration', 'Duration')}
                            value={formatDurationLabel(inquiry, t)}
                          />
                          <DetailCell
                            label={t('booking.guests_label', 'Guests')}
                            value={
                              inquiry.guests != null
                                ? String(inquiry.guests)
                                : t('booking.date_unavailable', 'Date unavailable')
                            }
                          />
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700 mb-3">
                          <span className="inline-flex items-center gap-1.5">
                            <Euro className="w-4 h-4 text-slate-500" aria-hidden />
                            <span className="text-slate-500">{t('booking.estimated_total', 'Estimated total')}:</span>
                            <span className="font-semibold text-slate-900">
                              {estimated || t('booking.estimated_unavailable', 'Estimate unavailable')}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs">
                            <Clock className="w-3.5 h-3.5" aria-hidden />
                            {t('booking.viewing_label', 'Viewing')}: {viewingStateLabel(inquiry.viewing_state, t)}
                          </span>
                        </div>

                        {inquiry.owner_response_at && (
                          <div className="text-xs text-slate-500 mb-2">
                            {t('booking.owner_responded_at', 'Owner responded')}:{' '}
                            <span className="text-slate-700">{formatTimestamp(inquiry.owner_response_at)}</span>
                          </div>
                        )}

                        {inquiry.owner_response && (
                          <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="text-xs font-medium text-slate-500 mb-1">
                              {t('booking.owner_response_label', 'Owner Response')}
                            </div>
                            <div className="text-slate-700 text-sm">{inquiry.owner_response}</div>
                          </div>
                        )}

                        {inquiry.refund_processed && (
                          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="text-xs font-medium text-purple-700 mb-1">
                              {t('booking.refund_processed_label', 'Refund Processed')}
                            </div>
                            <div className="text-slate-700 text-sm">
                              {translateTemplate(
                                t('booking.refund_processed_detail', '€{amount} refunded • Type: {type}'),
                                {
                                  amount: inquiry.refund_amount?.toFixed(2),
                                  type: inquiry.refund_type,
                                },
                              )}
                            </div>
                          </div>
                        )}

                        <GuestViewingSlotSelector
                          inquiry={inquiry}
                          onViewingReserved={loadInquiries}
                        />
                      </div>

                      <div className="md:w-52 bg-slate-50 p-4 flex flex-col justify-center gap-3 border-t md:border-t-0 md:border-l border-slate-200">
                        {needsPayment && paymentsEnabled && (
                          <>
                            <Button
                              onClick={() => handlePayDeposit(inquiry)}
                              disabled={processingPayment === inquiryRecordId}
                              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
                              data-testid="pay-deposit-btn"
                            >
                              {processingPayment === inquiryRecordId ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <CreditCard className="w-4 h-4 mr-2" />
                              )}
                              {t('booking.pay_deposit', 'Pay Deposit')}
                            </Button>
                            <div className="text-xs text-center text-slate-600">
                              {translateTemplate(
                                t('booking.deposit_amount_required', '€{amount} required'),
                                { amount: inquiry.deposit_amount?.toFixed(2) || '100.00' },
                              )}
                            </div>
                          </>
                        )}

                        {needsPayment && !paymentsEnabled && (
                          <p className="text-xs text-center text-slate-600">
                            {t('payments.booking_deposit_unavailable', 'Deposit payments are not available yet. Please contact support.')}
                          </p>
                        )}

                        {canEdit && (
                          <Button
                            variant="outline"
                            onClick={() => openEditInquiry(inquiry)}
                            className="w-full border-signal-blue/30 text-signal-blue hover:bg-signal-blue/10"
                            data-testid="edit-inquiry-btn"
                          >
                            {t('booking.edit_inquiry', 'Edit inquiry')}
                          </Button>
                        )}

                        {canWithdraw && (
                          <Button
                            variant="outline"
                            onClick={() => handleWithdrawInquiry(inquiryRecordId)}
                            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid="withdraw-inquiry-btn"
                          >
                            <X className="w-4 h-4 mr-2" />
                            {t('booking.withdraw_inquiry', 'Withdraw inquiry')}
                          </Button>
                        )}

                        {canRefund && paymentsEnabled && (
                          <Button
                            variant="outline"
                            onClick={() => handlePreviewRefund(inquiry)}
                            className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {t('booking.request_refund', 'Request Refund')}
                          </Button>
                        )}

                        <Link
                          to={buildListingDetailUrlFromInquiry(inquiry)}
                          className="text-center text-sm font-medium text-amber-700 hover:text-amber-800"
                          data-testid="view-listing-link"
                        >
                          {t('booking.view_listing', 'View Listing')} <ArrowRight className="w-3 h-3 inline" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Edit inquiry modal */}
        {editingInquiry && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-slate-800">{t('booking.edit_inquiry', 'Edit inquiry')}</CardTitle>
                <CardDescription>{editingInquiry.listing_title}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">{t('booking.check_in', 'Check-in')}</label>
                    <input
                      type="date"
                      value={editForm.check_in_date}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, check_in_date: e.target.value }))}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">{t('booking.check_out', 'Check-out')}</label>
                    <input
                      type="date"
                      value={editForm.check_out_date}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, check_out_date: e.target.value }))}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">{t('booking.guests', 'Number of Guests')}</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editForm.guests}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, guests: parseInt(e.target.value, 10) || 1 }))}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">{t('booking.message_to_owner', 'Message to Owner')}</label>
                  <textarea
                    value={editForm.message}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, message: e.target.value }))}
                    rows={3}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">{t('booking.special_requests', 'Special Requests (optional)')}</label>
                  <textarea
                    value={editForm.special_requests}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, special_requests: e.target.value }))}
                    rows={2}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setEditingInquiry(null)} className="flex-1">
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    onClick={handleSaveInquiryEdit}
                    disabled={savingEdit}
                    className="flex-1 bg-signal-blue hover:bg-signal-blue-hover text-white"
                  >
                    {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('booking.save_inquiry', 'Save changes')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Refund Modal */}
        {showRefundModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  {t('booking.refund_modal.title', 'Refund Request')}
                </CardTitle>
                <CardDescription>
                  {t('booking.refund_modal.description', 'Review the refund breakdown before proceeding')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRefund && !refundPreview ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-gold-400 animate-spin" />
                  </div>
                ) : refundPreview ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                      <div className="flex justify-between text-slate-600">
                        <span>{t('booking.refund_modal.original_deposit', 'Original Deposit')}</span>
                        <span className="font-medium text-slate-900">€{refundPreview.original_deposit?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2 text-slate-600">
                        <span>{t('booking.refund_modal.refund_type', 'Refund Type')}</span>
                        <Badge className={
                          refundPreview.refund_type === 'full' ? 'bg-green-100 text-green-800 border-green-200' :
                          refundPreview.refund_type === 'partial' ? 'bg-amber-100 text-amber-900 border-amber-200' :
                          refundPreview.refund_type === 'minimal' ? 'bg-orange-100 text-orange-900 border-orange-200' :
                          'bg-red-100 text-red-800 border-red-200'
                        }>
                          {translateTemplate(
                            t('booking.refund_modal.refund_type_value', '{type} ({percent}%)'),
                            {
                              type: getRefundTypeLabel(refundPreview.refund_type),
                              percent: refundPreview.refund_percent,
                            },
                          )}
                        </Badge>
                      </div>
                      <hr className="border-slate-200" />
                      <div className="flex justify-between text-slate-600">
                        <span>{t('booking.refund_modal.platform_fee', 'Platform Fee')}</span>
                        <span className="font-medium text-red-600">-€{refundPreview.platform_fee?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>{t('booking.refund_modal.owner_fee', 'Owner Fee')}</span>
                        <span className="font-medium text-amber-700">-€{refundPreview.owner_fee?.toFixed(2)}</span>
                      </div>
                      <hr className="border-slate-200" />
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-slate-900">{t('booking.refund_modal.your_refund', 'Your Refund')}</span>
                        <span className="text-emerald-600">€{refundPreview.refund_amount?.toFixed(2)}</span>
                      </div>
                    </div>

                    {refundPreview.days_until_checkin !== undefined && (
                      <div className="text-sm text-slate-600 text-center">
                        {translateTemplate(
                          t('booking.refund_modal.days_until_checkin', '{days} days until check-in'),
                          { days: refundPreview.days_until_checkin },
                        )}
                      </div>
                    )}

                    {refundPreview.refund_type === 'none' && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          <span className="text-sm">
                            {t('booking.refund_modal.none_available', 'No refund available at this time')}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowRefundModal(null);
                          setRefundPreview(null);
                        }}
                        className="flex-1"
                      >
                        {t('common.cancel', 'Cancel')}
                      </Button>
                      <Button
                        onClick={handleProcessRefund}
                        disabled={loadingRefund || refundPreview.refund_amount === 0}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                      >
                        {loadingRefund ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        {t('booking.refund_modal.confirm', 'Confirm Refund')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
